'use client';

import { allSortMethod } from '@/constants/all-sort-method';
import useIsMobile from '@/hooks/use-is-mobile';
import { PopoverTrigger } from '@radix-ui/react-popover';
import clsx from 'clsx';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRef, useState } from 'react';
import HoverPrefetchLink from '../hover-prefetch-link';
import { Popover, PopoverContent } from '../ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const ProductSort = props => {
  const { availableSortMethods, sortState, isSearchPage = false, inFilterBar = false } = props;
  const isMobile = useIsMobile();
  const triggerRef = useRef();
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const excludePaginationPathParts = pathname.split('/').filter((x, i, arr) => {
    return !!x && x !== 'p' && (!/\d+/.test(x) || (/\d+/.test(x) && i > 0 && arr[i - 1] !== 'p'));
  });

  const sortMethodsFromConfig = availableSortMethods
    ? availableSortMethods
        .map(method => {
          const { value, label } = method;

          if (value !== 'price') {
            return {
              id: `sortItem.${value}`,
              text: label,
              attribute: value,
              direction:
                value === 'high_to_low' || value === 'most_reviews' || value === 'created_at' || value === 'best_seller'
                  ? 'DESC'
                  : 'ASC',
            };
          }
        })
        .filter(method => !!method)
    : [];

  const generateLinkHref = (attribute, prevActive) => {
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete('sort_by');
    const sortQuery = allSortMethod.find(x => x.sortAttribute === attribute)?.sortQuery;

    if (!prevActive && sortQuery) {
      nextSearchParams.append('sort_by', sortQuery);
    }

    if (isSearchPage) {
      nextSearchParams.delete('page');
    }

    const nextQueryStr = nextSearchParams.toString();

    if (isSearchPage) {
      return `/search.html?${nextQueryStr}`;
    }

    return `/${excludePaginationPathParts.join('/')}` + (nextQueryStr ? `?${nextQueryStr}` : '');
  };

  const sortElements = (
    <ul className='bg-white'>
      {Array.from(sortMethodsFromConfig, (sortItem, i) => {
        const { attribute, direction } = sortItem;

        const isActive = sortState.sortAttribute === attribute && sortState.sortDirection === direction;
        const key = `${attribute}--${direction}`;

        return (
          <li
            key={key}
            className='px-3'
          >
            <HoverPrefetchLink
              onClick={() => {
                setOpen(false);
              }}
              className={clsx(
                i !== 0 && 'border-t-1 border-solid border-E7DED5',
                'flex w-full items-center justify-between gap-x-5 py-[15px]',
              )}
              href={generateLinkHref(attribute, isActive)}
            >
              <span className='text-sm leading-5'>{sortItem.text}</span>
              <div
                className={clsx(
                  ' h-5 w-5 ',
                  'border-1 border-solid ',
                  'flex items-center justify-center',
                  isActive ? 'border-80583F bg-80583F' : 'border-E7DED5',
                )}
              >
                {isActive ? (
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='12'
                    fill='none'
                    viewBox='0 0 15 12'
                  >
                    <path
                      stroke='#fff'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='M13.5 1 5.1 11 1.5 7.25'
                    />
                  </svg>
                ) : null}
              </div>
            </HoverPrefetchLink>
          </li>
        );
      })}
    </ul>
  );

  const sortTriggerBtn = (
    <button
      priority={'low'}
      className={clsx(
        'flex flex-1 items-center justify-center bg-f8f3ef text-base leading-4 lg_h-11 lg_w-[174px] lg_flex-none lg_gap-x-2.5',
        inFilterBar ? 'h-auto gap-x-[5px] rounded-[3px] py-2.5 lg_py-0' : 'h-[50px] gap-x-2.5',
      )}
      type='button'
    >
      <span
        className={clsx(
          'text-80583F lg_text-base lg_leading-6',
          inFilterBar ? 'text-[13px] font-medium leading-[1.4]  ' : 'text-[17px]  leading-6',
        )}
      >
        SORT BY
      </span>
      {inFilterBar ? (
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='10'
          height='15'
          fill='none'
          viewBox='0 0 10 15'
        >
          <path
            fill='#80583F'
            d='M10 5H7.93L5 7.93 2.07 5H0l5 5z'
          ></path>
        </svg>
      ) : (
        <span
          className={clsx(
            ' relative h-3 w-3 ',
            'before_content-[""] after_content-[""]',
            'before_h-0.5 before_w-full after_h-0.5 after_w-full',
            'before_bg-80583F',
            'before_absolute before_left-1/2 before_top-1/2 before_-translate-x-1/2 before_-translate-y-1/2',
            'after_absolute after_left-1/2 after_top-1/2 after_-translate-x-1/2 after_-translate-y-1/2',
            'after_rotate-90',
            open ? 'after_bg-transparent' : 'after_bg-80583F',
          )}
        />
      )}
    </button>
  );

  return isMobile ? (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>{sortTriggerBtn}</PopoverTrigger>
      <PopoverContent
        sideOffset={inFilterBar ? 10 : 0}
        className={clsx('shadow-md', inFilterBar ? 'z-[70]' : '')}
      >
        {sortElements}
      </PopoverContent>
    </Popover>
  ) : (
    <TooltipProvider>
      <Tooltip
        open={open}
        onOpenChange={setOpen}
        delayDuration={200}
      >
        <TooltipTrigger
          onClick={e => {
            e.preventDefault();
          }}
          asChild
          onPointerDown={event => event.preventDefault()}
          ref={triggerRef}
        >
          {sortTriggerBtn}
        </TooltipTrigger>
        <TooltipContent
          onPointerDownOutside={e => {
            if (triggerRef.current?.contains(e.target)) {
              e.preventDefault();
            }
          }}
          side='bottom'
          className='border-none p-0 shadow-md'
        >
          {sortElements}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default ProductSort;
